import { useQuery } from "react-query";
import { useAccessToken } from "@modules/forme/queries";
import {
  getAssignableUsers,
  getCaseTemplateDetail,
  getCaseTemplates,
  getHouseholds,
  getWorkItemDetail,
  getWorkItems,
} from "./client";
import { WorkItemKind } from "./types";

// WORK_ITEMS_KEY is deliberately NOT the Command Center card's key: this page writes
// optimistically, and sharing one would land its richer rows in the card's cache.
const WORK_ITEMS_KEY = "casesTasksWorkItems";
const WORK_ITEM_DETAIL_KEY = "casesTasksWorkItemDetail";
const CASE_TEMPLATES_KEY = "casesTasksCaseTemplates";
const ASSIGNABLE_USERS_KEY = "casesTasksAssignableUsers";
const CASE_TEMPLATE_DETAIL_KEY = "casesTasksCaseTemplateDetail";
const HOUSEHOLD_SEARCH_KEY = "casesTasksHouseholdSearch";

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

const browserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;

// Load-bearing, not a perf tweak: the backend serves a nightly snapshot, so a refetch
// buys no freshness and discards the optimistic writes an edit depends on.
const noBackgroundRefetch = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

// Search, sort and filters are applied client-side from this one response, so they stay
// out of the query key.
const useWorkItems = (viewAsAdvisorEmail?: string) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";
  const timezone = browserTimezone();

  return useQuery(
    [WORK_ITEMS_KEY, viewAsAdvisorEmail ?? "self", timezone],
    () =>
      getWorkItems(accessToken, {
        timezone,
        advisorEmail: viewAsAdvisorEmail,
      }),
    {
      enabled: !!accessToken,
      staleTime: FIVE_MINUTES,
      keepPreviousData: true,
      ...noBackgroundRefetch,
    }
  );
};

interface UseWorkItemDetailArgs {
  kind: WorkItemKind;
  id: string;
  viewAsAdvisorEmail?: string;
  // Only while expanded, or a 200-row list fires 200 requests on mount.
  enabled: boolean;
}

const useWorkItemDetail = ({
  kind,
  id,
  viewAsAdvisorEmail,
  enabled,
}: UseWorkItemDetailArgs) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";
  const timezone = browserTimezone();

  return useQuery(
    [WORK_ITEM_DETAIL_KEY, kind, id, viewAsAdvisorEmail ?? "self", timezone],
    () =>
      getWorkItemDetail(accessToken, kind, id, {
        timezone,
        advisorEmail: viewAsAdvisorEmail,
      }),
    {
      enabled: !!accessToken && !!id && enabled,
      staleTime: FIVE_MINUTES,
      // Re-expanding must not refetch: a PATCH has already written the authoritative
      // record here, and the snapshot holds the pre-edit value.
      refetchOnMount: false,
      ...noBackgroundRefetch,
    }
  );
};

// Near-static, and only needed once the modal is open.
const useCaseTemplates = (viewAsAdvisorEmail?: string, enabled = true) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";

  return useQuery(
    [CASE_TEMPLATES_KEY, viewAsAdvisorEmail ?? "self"],
    () => getCaseTemplates(accessToken, { advisorEmail: viewAsAdvisorEmail }),
    {
      enabled: !!accessToken && enabled,
      staleTime: THIRTY_MINUTES,
      ...noBackgroundRefetch,
    }
  );
};

// The caller debounces `search` and passes enabled=false until its picker is open.
const useAssignableUsers = (
  search: string,
  viewAsAdvisorEmail?: string,
  enabled = true
) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";
  const trimmed = search.trim();

  return useQuery(
    [ASSIGNABLE_USERS_KEY, viewAsAdvisorEmail ?? "self", trimmed],
    () =>
      getAssignableUsers(accessToken, trimmed, {
        advisorEmail: viewAsAdvisorEmail,
      }),
    {
      enabled: !!accessToken && enabled,
      staleTime: FIVE_MINUTES,
      keepPreviousData: true,
      ...noBackgroundRefetch,
    }
  );
};

// Fetched once a template is picked, for the modal's preview.
const useCaseTemplateDetail = (
  templateId: string | null,
  viewAsAdvisorEmail?: string
) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";

  return useQuery(
    [CASE_TEMPLATE_DETAIL_KEY, templateId, viewAsAdvisorEmail ?? "self"],
    () =>
      getCaseTemplateDetail(accessToken, templateId as string, {
        advisorEmail: viewAsAdvisorEmail,
      }),
    {
      enabled: !!accessToken && !!templateId,
      staleTime: THIRTY_MINUTES,
      ...noBackgroundRefetch,
    }
  );
};

// Scoped to the advisor's account team server-side. Two-character minimum so opening
// the picker doesn't pull the whole book.
const useHouseholdSearch = (
  search: string,
  viewAsAdvisorEmail?: string,
  enabled = true
) => {
  const { data: tokenData } = useAccessToken();
  const accessToken = tokenData?.accessToken ?? "";
  const trimmed = search.trim();

  return useQuery(
    [HOUSEHOLD_SEARCH_KEY, viewAsAdvisorEmail ?? "self", trimmed],
    () =>
      getHouseholds(accessToken, trimmed, { advisorEmail: viewAsAdvisorEmail }),
    {
      enabled: !!accessToken && enabled && trimmed.length >= 2,
      staleTime: FIVE_MINUTES,
      keepPreviousData: true,
      ...noBackgroundRefetch,
    }
  );
};

export {
  useWorkItems,
  useWorkItemDetail,
  useCaseTemplates,
  useCaseTemplateDetail,
  useAssignableUsers,
  useHouseholdSearch,
  WORK_ITEMS_KEY,
  WORK_ITEM_DETAIL_KEY,
  CASE_TEMPLATES_KEY,
  CASE_TEMPLATE_DETAIL_KEY,
  ASSIGNABLE_USERS_KEY,
  HOUSEHOLD_SEARCH_KEY,
};
